// src/components/events/EventDetails.tsx

import React, { useEffect, useState } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useParams } from 'react-router-dom';
import { EventType } from '../../types/Event';
// DELETED: import EventAnalytics from './EventAnalytics';
import SparkLayout from '../layouts/SparkLayout';
import SparkCard from '../common/SparkCard';
import SparkButton from '../common/SparkButton';
import SparkHeading from '../common/SparkHeading';
import { TemplateType } from '../../types/Template';
import ManageRecipientsCard from './ManageRecipientsCard';
import useEventPaywall from '../../hooks/useEventPaywall';
import UnlockSalesModal from '../billing/UnlockSalesModal';
import CheckoutStatusHandler from '../billing/CheckoutStatusHandler';

const EventDetails: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<EventType | null>(null);
  const [, setTemplate] = useState<TemplateType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Enhanced paywall integration
  const { unlocked, loading: paywallLoading, anonymousLink } = useEventPaywall(eventId);

  // State for invitation statistics, now including people counts
  const [invitationStats, setInvitationStats] = useState({
    accepted: 0,
    declined: 0,
    pending: 0,
    totalPeople: 0,
    anonymous: {
      views: 0,
      accepted: 0,
      declined: 0,
      people: 0,
    },
  });

  // Fetch event and template data
  useEffect(() => {
    if (!eventId) return;

    const fetchEvent = async () => {
      setLoading(true);
      try {
        const ref = doc(db, 'events', eventId);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setError('Event not found');
        } else {
          const data = snap.data() as EventType;
          const eventData = { ...data, id: snap.id };
          setEvent(eventData);

          if (data.templateId) {
            const templateRef = doc(db, 'templates', data.templateId);
            const templateSnap = await getDoc(templateRef);
            if (templateSnap.exists()) {
              const templateData = templateSnap.data() as TemplateType;
              setTemplate({ ...templateData, id: templateSnap.id });
            } else {
              console.warn(`Template with ID ${data.templateId} not found`);
            }
          }
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load event details.');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  // Fetch invitation statistics once the event is loaded
  useEffect(() => {
    const fetchInvitationStats = async () => {
      if (!event || !event.id) return;

      try {
        const invitationsRef = collection(db, 'invitations');
        const regularQuery = query(
          invitationsRef,
          where('eventId', '==', event.id),
          where('recipientName', '!=', 'Anonymous')
        );
        const regularSnapshot = await getDocs(regularQuery);

        let accepted = 0;
        let declined = 0;
        let pending = 0;
        let totalPeople = 0;

        regularSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.status === 'accepted') {
            accepted++;
            totalPeople += data.peopleCount || 1;
          } else if (data.status === 'declined') {
            declined++;
          } else {
            pending++;
          }
        });

        const anonQuery = query(
          invitationsRef,
          where('eventId', '==', event.id),
          where('recipientName', '==', 'Anonymous')
        );
        const anonSnapshot = await getDocs(anonQuery);

        let anonStats = {
          views: 0,
          accepted: 0,
          declined: 0,
          people: 0,
        };

        if (!anonSnapshot.empty) {
          const anonData = anonSnapshot.docs[0].data();
          anonStats.views = anonData.viewsCount || 0;
          anonStats.accepted = anonData.acceptedCount || 0;
          anonStats.declined = anonData.declinedCount || 0;
          anonStats.people = anonData.peopleCount || 0;
        }

        // Add anonymous people to total
        totalPeople += anonStats.people;

        setInvitationStats({
          accepted,
          declined,
          pending,
          totalPeople,
          anonymous: anonStats,
        });
      } catch (err) {
        console.error('Error fetching invitation stats:', err);
      }
    };

    fetchInvitationStats();
  }, [event]);

  if (loading) {
    return (
      <SparkLayout>
        <SparkCard
          style={{
            position: 'relative',
            overflow: 'auto',
            maxHeight: '85vh',
            width: '92%',
            maxWidth: '64rem',
            margin: '0 auto 2.5rem auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '40vh',
              width: '100%',
            }}
          >
            <div className="spark-spinner" style={{ marginBottom: '1rem' }} />
            <p
              style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: 'var(--color-text-primary)',
              }}
            >
              Loading event details...
            </p>
          </div>
        </SparkCard>
      </SparkLayout>
    );
  }

  if (error) {
    return (
      <SparkLayout>
        <SparkCard
          style={{
            position: 'relative',
            overflow: 'auto',
            maxHeight: '85vh',
            width: '92%',
            maxWidth: '64rem',
            margin: '0 auto 2.5rem auto',
          }}
        >
          <div className="spark-message spark-message--error">{error}</div>
        </SparkCard>
      </SparkLayout>
    );
  }

  if (!event) return null;

  const dateStr = event.eventDate?.toDate
    ? event.eventDate.toDate().toLocaleDateString()
    : 'No date';
  const timeStr = event.eventTime || '';
  const dateTimeStr = timeStr ? `${dateStr} at ${timeStr}` : dateStr;

  // Enhanced invitation link handling
  const invitationLink = anonymousLink || (unlocked ? `${window.location.origin}/invite/${event.id}?id=${event.id}` : '');

  const handleOpenInvitation = () => {
    if (unlocked && invitationLink) {
      window.open(invitationLink, '_blank');
    } else {
      // Dispatch event to open the modal
      window.dispatchEvent(new CustomEvent('spark:openUnlockModal'));
    }
  };

  return (
    <SparkLayout>
      <CheckoutStatusHandler eventId={event.id!}>
        {/* Single UnlockSalesModal instance that listens for global events */}
        <UnlockSalesModal eventId={event.id!} listenGlobalOpen={true} />
        
        <SparkCard
          style={{
            position: 'relative',
            overflow: 'auto',
            maxHeight: '85vh',
            width: '92%',
            maxWidth: '64rem',
            margin: '0 auto 2.5rem auto',
            padding: '2rem 1.5rem',
            boxSizing: 'border-box',
          }}
        >

          {/* Event Title */}
          <SparkHeading
            level="h1"
            glow
            style={{
              fontSize: '2.5rem',
              fontWeight: 900,
              textAlign: 'center',
              marginBottom: '1.5rem',
              wordBreak: 'break-word',
            }}
          >
            {event.title}
          </SparkHeading>

          {/* Event Details Section */}
          <SparkCard variant="solid" style={{ marginBottom: '2rem', width: '100%' }}>
            <div
              style={{
                textAlign: 'center',
                width: '100%',
                wordWrap: 'break-word',
              }}
            >
              <p
                style={{
                  fontSize: '1.5rem',
                  marginBottom: '0.75rem',
                  lineHeight: 1.4,
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  color: '#000',
                }}
              >
                <span style={{ fontWeight: 'bold' }}>Date:</span> {dateTimeStr}
              </p>
              <p
                style={{
                  fontSize: '1.25rem',
                  marginBottom: '1rem',
                  lineHeight: 1.4,
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  color: '#000',
                }}
              >
                <span style={{ fontWeight: 'bold' }}>Location:</span>{' '}
                {event.location || 'No location specified'}
              </p>
              {event.description && (
                <p
                  style={{
                    fontSize: '1.125rem',
                    fontStyle: 'italic',
                    marginTop: '1rem',
                    lineHeight: 1.5,
                    color: '#000',
                  }}
                >
                  "{event.description}"
                </p>
              )}
            </div>
          </SparkCard>

          {/* Enhanced Invitation Actions Section */}
          <div style={{ width: '100%', marginBottom: '2rem' }}>
            <SparkHeading
              level="h2"
              style={{
                fontSize: '1.75rem',
                fontWeight: 900,
                marginBottom: '1rem',
                textAlign: 'center',
              }}
            >
              Invitation Actions
            </SparkHeading>
            <SparkCard variant="solid">
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  padding: '1rem',
                }}
              >
                {/* OPEN INVITATION LINK button with proper paywall handling */}
                <SparkButton
                  onClick={handleOpenInvitation}
                  style={{
                    width: '100%',
                    fontSize: '1.125rem',
                    padding: '1rem',
                  }}
                >
                  {unlocked ? 'OPEN INVITATION LINK' : 'UNLOCK TO OPEN INVITATION LINK'}
                </SparkButton>
                
                <SparkButton
                  to={`/create-event?eventId=${event.id}&step=9`}
                  variant="secondary"
                  style={{
                    width: '100%',
                    fontSize: '1.125rem',
                    padding: '1rem',
                    textAlign: 'center',
                  }}
                >
                  VIEW & EDIT INVITATION
                </SparkButton>
              </div>
            </SparkCard>
          </div>

          {/* Manage Recipients Card Section */}
          <div
            style={{
              width: '100%',
              marginBottom: '1.5rem',
            }}
          >
            <ManageRecipientsCard
              eventId={event.id!}
              recipientCount={
                invitationStats.pending +
                invitationStats.accepted +
                invitationStats.declined
              }
              responseData={invitationStats}
            />
          </div>

          {/* Button row for Manage Guests */}
          <div
            style={{
              display: 'flex',
              width: '100%',
              marginBottom: '1.5rem',
              justifyContent: 'center',
            }}
          >
            <SparkButton
              to={`/event/${event.id}/guests`}
              size="large"
              style={{
                width: '100%',
                maxWidth: '100%',
                fontSize: '1.25rem',
                padding: '1rem',
              }}
            >
              GUESTS & INVITES
            </SparkButton>
          </div>

          {/* Navigation Buttons - Dashboard and Edit */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '1rem',
              marginBottom: '1.5rem',
            }}
          >
            <SparkButton to="/dashboard" style={{ width: '48%' }}>
              DASHBOARD
            </SparkButton>
            <SparkButton to={`/create-event?eventId=${event.id}`} style={{ width: '48%' }}>
              EDIT
            </SparkButton>
          </div>

          {/* DELETED the EventAnalytics component from here */}
          
        </SparkCard>
      </CheckoutStatusHandler>
    </SparkLayout>
  );
};

export default EventDetails;